import { CONTACTS } from "../../constants"
import { usePhone } from "../../theme/PhoneContext"
import { useTheme } from "../../theme/useTheme"
import { PHONE_CODE_LENGTH, PHONE_REST_DIGITS, formatPhoneRest } from "../../utils/phone"
import { Link } from "../ui/Link"
import { Icon } from "./icons"
import { Box, Snackbar, Typography, alpha } from "@mui/material"
import type { Theme } from "@mui/material/styles"
import { useState } from "react"

interface ContactsProps {
    variant?: "header" | "footer"
}

export const Contacts = ({ variant = "header" }: ContactsProps) => {
    const { layout, breakpoints, audience } = useTheme()
    const phone = usePhone()
    const [snackbarOpen, setSnackbarOpen] = useState(false)
    const [snackbarKey, setSnackbarKey] = useState(0)
    const [copiedText, setCopiedText] = useState("")
    const isFooter = variant === "footer"
    // body2, not caption — caption read too small; no alpha tint (contacts
    // are reading content)
    const textVariant = "body2"
    const iconStyle = {
        fontSize: "1em",
        marginRight: "0.2em",
    }
    // every row is the same flex lockup; interactive ones swap the Box for
    // an anchor/button of the same geometry, so the whole visible lockup is
    // one hit target with no invisible padding bands
    const rowTargetSx = {
        display: "flex",
        alignItems: "center",
        // container's font size — the anchor's body2 variant would shrink it
        fontSize: "inherit",
        height: "1.2em",
        lineHeight: "1.2em",
    }
    // strip UA button chrome so copy rows align with plain rows
    const buttonResetSx = {
        background: "none",
        border: 0,
        margin: 0,
        padding: 0,
        color: "inherit",
        textAlign: "inherit",
        cursor: "pointer",
    }
    // icon in text.primary, label in text.secondary — pinned because the
    // anchor renders at body2 with its own link colour
    const iconInTargetSx = {
        display: "inline-flex",
        alignItems: "center",
        color: (t: Theme) => t.palette.text.primary,
    }
    const labelInTargetSx = {
        fontSize: "0.875em",
        // unitless — inheriting the row's 1.2em line box shifts glyphs up
        lineHeight: 1.43,
        color: (t: Theme) => t.palette.text.secondary,
    }

    const boxSx = isFooter
        ? {
              display: "flex" as const,
              flexWrap: "wrap" as const,
              columnGap: 0.75,
              rowGap: 0.375 * layout.verticalSpacing,
              mt: 0,
              justifyContent: "center" as const,
          }
        : {
              display: "flex" as const,
              flexWrap: "wrap" as const,
              columnGap: 1,
              rowGap: 0.5 * layout.verticalSpacing,
              mt: 0,
              justifyContent: !breakpoints.isFullPage ? ("center" as const) : ("left" as const),
              width: !breakpoints.isFullPage ? "100%" : 170,
          }

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text)
        setCopiedText(text)
        setSnackbarOpen(true)
        setSnackbarKey((prev) => prev + 1)
    }

    // phone row: typed digits normal ink, remaining zeros a dim backdrop
    // ("+(60)8-000-0000") — click-to-copy in every state, copying the
    // displayed string. formatPhoneRest is prefix-stable (dashes at fixed
    // positions), so zero-padding the digits before formatting yields the
    // dim tail after the typed part
    const restLength = PHONE_REST_DIGITS.reduce((a, b) => a + b, 0)
    const paddedRest = formatPhoneRest(phone.rest + "0".repeat(restLength - phone.rest.length))
    const typedRest = formatPhoneRest(phone.rest)
    const dimCode = "0".repeat(PHONE_CODE_LENGTH - phone.code.length)
    const dimRest = paddedRest.slice(typedRest.length)
    const phoneDisplay = `+(${phone.code}${dimCode})${paddedRest}`
    const dimSpan = {
        component: "span" as const,
        sx: { color: (t: Theme) => alpha(t.palette.text.primary, 0.2) },
    }
    return (
        <>
            <Box sx={boxSx}>
                {/* accessible name shared with SocialRow's same-href button
                    (axe identical-links-same-purpose), containing the visible
                    handle text (label-content-name-mismatch) */}
                <Link
                    href={CONTACTS.linkedinHref}
                    aria-label={`LinkedIn (${CONTACTS.linkedinHandle})`}
                    variant={textVariant}
                    sx={rowTargetSx}
                >
                    <Box component="span" sx={iconInTargetSx}>
                        <Icon name="linkedin" style={iconStyle} />
                    </Box>
                    <Box component="span" sx={labelInTargetSx}>
                        {CONTACTS.linkedinHandle}
                    </Box>
                </Link>
                <Link
                    href={CONTACTS.githubHref}
                    aria-label={`GitHub (${CONTACTS.githubHandle})`}
                    variant={textVariant}
                    sx={rowTargetSx}
                >
                    <Box component="span" sx={iconInTargetSx}>
                        <Icon name="github" style={iconStyle} />
                    </Box>
                    <Box component="span" sx={labelInTargetSx}>
                        {CONTACTS.githubHandle}
                    </Box>
                </Link>
                {phone.show && (
                    <Typography
                        component="button"
                        variant={textVariant}
                        onClick={() => copyToClipboard(phoneDisplay)}
                        sx={{ ...buttonResetSx, ...rowTargetSx }}
                    >
                        <Box component="span" sx={iconInTargetSx}>
                            <Icon name="phone" style={iconStyle} />
                        </Box>
                        <Box component="span" sx={labelInTargetSx}>
                            +({phone.code}
                            {dimCode && <Box {...dimSpan}>{dimCode}</Box>}){typedRest}
                            {dimRest && <Box {...dimSpan}>{dimRest}</Box>}
                        </Box>
                    </Typography>
                )}
                {!isFooter && (
                    <Box sx={rowTargetSx}>
                        <Box component="span" sx={iconInTargetSx}>
                            <Icon name="locationDot" style={iconStyle} />
                        </Box>
                        <Box component="span" sx={labelInTargetSx}>
                            {CONTACTS.location}
                        </Box>
                    </Box>
                )}
                {!isFooter && audience.mode === "international" && (
                    <Box sx={rowTargetSx}>
                        <Box component="span" sx={iconInTargetSx}>
                            <Icon name="passport" style={iconStyle} />
                        </Box>
                        <Box component="span" sx={labelInTargetSx}>
                            Thai
                        </Box>
                    </Box>
                )}
                <Link href={CONTACTS.siteHref} variant={textVariant} sx={rowTargetSx}>
                    <Box component="span" sx={iconInTargetSx}>
                        <Icon name="globe" style={iconStyle} />
                    </Box>
                    <Box component="span" sx={labelInTargetSx}>
                        {CONTACTS.siteLabel}
                    </Box>
                </Link>
                <Typography
                    component="button"
                    variant={textVariant}
                    onClick={() => copyToClipboard(CONTACTS.email)}
                    sx={{ ...buttonResetSx, ...rowTargetSx }}
                >
                    <Box component="span" sx={iconInTargetSx}>
                        <Icon name="envelope" style={iconStyle} />
                    </Box>
                    <Box component="span" sx={labelInTargetSx}>
                        {CONTACTS.email}
                    </Box>
                </Typography>
            </Box>
            <Snackbar
                className="no-print"
                key={snackbarKey}
                open={snackbarOpen}
                autoHideDuration={5000}
                onClose={() => setSnackbarOpen(false)}
                message={`Copied "${copiedText}" to clipboard!`}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                sx={{ "& .MuiSnackbarContent-message": { textAlign: "center", width: "100%" } }}
            />
        </>
    )
}
